import { Dialog, LinearProgress } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { getCPDataWithoutParams } from 'src/clinic/services'
import { LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED } from 'src/consumer/constants'
import { deleteERMDatabase } from 'src/emr/database/indexedDb'
import {
  addOrUpdateAdvicesInIdb,
  addOrUpdateDiagnosisInIdb,
  addOrUpdateDrugsInIdb,
  addOrUpdateGenericDrugsInIdb,
  addOrUpdateHealthComplaintsInIdb,
  addOrUpdateInstructionsInIdb,
  addOrUpdateInvestigationInIdb,
  addOrUpdateObservationsInIdb,
  addOrUpdatePatientsInIdb,
  addOrUpdatePsychAssessmentsInIdb,
} from 'src/emr/database/indexedDbDao'
import { getMPDataWithoutParams } from 'src/mentor/services'

// EMR Dialog Box:
export const ERMOptionsSyncComponent = ({ takeActionsItself, mentorClinicAssociationData }) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [syncProgress, setSyncProgress] = useState({})
  const [saving, setSaving] = useState({})

  // Ensure clinicBrandIds is unique
  const clinicBrandUniqueIds = [
    ...new Set(
      mentorClinicAssociationData?.map((item) => item?.clinicBrandId && item?.clinicBrandId),
    ),
  ]

  // Helper function to shorten brand ID
  function formatClinicBrandId(id) {
    const foundClinic = (mentorClinicAssociationData || [])?.find(
      (item) => item?.clinicBrandId === id,
    )
    if (foundClinic?.clinicBrandName) {
      return foundClinic?.clinicBrandName
    }
    return `clinic-${id && id?.slice(-3)}`
  }

  async function updateEverythingEveryBrand(
    updateDrugs,
    updateComplaints,
    updateDiagnosis,
    updateObservations,
    updateAdvices,
    updateInstructions,
    updateInvestigation,
    updatePatients,
    updatePsychAssessments,
    updateGenericDrugs,

    drugsModifiedAfterTimeUtc,
    healthComplaintsModifiedAfterTimeUtc,
    diagnosisModifiedAfterTimeUtc,
    observationsModifiedAfterTimeUtc,
    advicesModifiedAfterTimeUtc,
    instructionsModifiedAfterTimeUtc,
    investigationModifiedAfterTimeUtc,
    patientsModifiedAfterTimeUtc,
    psychAssessmentsModifiedAfterTimeUtc,
    genericDrugsModifiedAfterTimeUtc,
  ) {
    setSaving({
      drugs: true,
      complaints: true,
      diagnosis: true,
      observations: true,
      advices: true,
      instructions: true,
      investigation: true,
      patients: true,
      psychAssessments: true,
      genericDrugs: true,
    })

    try {
      // Process each clinicBrandId
      for (const clinicBrandId of clinicBrandUniqueIds) {
        // 1. Sync drugs
        if (updateDrugs) {
          await fetchDataAndSync(
            `drugs-${clinicBrandId}`,
            `/api/secure/drug/list?search=${''}&clinicBrandId=${clinicBrandId}`,
            addOrUpdateDrugsInIdb,
            drugsModifiedAfterTimeUtc,
          )
          localStorage.setItem(
            LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.DRUGS,
            dayjs.utc().format('YYYY-MM-DD HH:mm:ss'),
          )
        }

        // 2. Sync health complaints
        if (updateComplaints) {
          await fetchDataAndSync(
            `complaints-${clinicBrandId}`,
            `/api/secure/clinic-practitioner/health-complaint/list?search=${''}&clinicBrandId=${clinicBrandId}`,
            addOrUpdateHealthComplaintsInIdb,
            healthComplaintsModifiedAfterTimeUtc,
          )
          localStorage.setItem(
            LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.COMPLAINTS,
            dayjs.utc().format('YYYY-MM-DD HH:mm:ss'),
          )
        }

        // 3. Sync diagnosis
        if (updateDiagnosis) {
          await fetchDataAndSync(
            `diagnosis-${clinicBrandId}`,
            `/api/secure/clinic-practitioner/diagnosis/list?search=${''}&clinicBrandId=${clinicBrandId}`,
            addOrUpdateDiagnosisInIdb,
            diagnosisModifiedAfterTimeUtc,
          )
          localStorage.setItem(
            LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.DIAGNOSIS,
            dayjs.utc().format('YYYY-MM-DD HH:mm:ss'),
          )
        }

        // 4. Sync observations
        if (updateObservations) {
          await fetchDataAndSync(
            `observations-${clinicBrandId}`,
            `/api/secure/clinic-practitioner/health-observation/list?search=${''}&clinicBrandId=${clinicBrandId}`,
            addOrUpdateObservationsInIdb,
            observationsModifiedAfterTimeUtc,
          )
          localStorage.setItem(
            LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.OBSERVATIONS,
            dayjs.utc().format('YYYY-MM-DD HH:mm:ss'),
          )
        }

        // 5. Sync advices
        if (updateAdvices) {
          await fetchDataAndSync(
            `advices-${clinicBrandId}`,
            `/api/secure/clinic-practitioner/prescription-advice/list?search=${''}&clinicBrandId=${clinicBrandId}`,
            addOrUpdateAdvicesInIdb,
            advicesModifiedAfterTimeUtc,
          )
          localStorage.setItem(
            LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.ADVICES,
            dayjs.utc().format('YYYY-MM-DD HH:mm:ss'),
          )
        }

        // 6. Sync instructions
        if (updateInstructions) {
          await fetchDataAndSync(
            `instruction-${clinicBrandId}`,
            `/api/secure/clinic-practitioner/instruction/list?search=${''}&clinicBrandId=${clinicBrandId}`,
            addOrUpdateInstructionsInIdb,
            instructionsModifiedAfterTimeUtc,
          )
          localStorage.setItem(
            LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.INSTRUCTIONS,
            dayjs.utc().format('YYYY-MM-DD HH:mm:ss'),
          )
        }

        // 7. Sync investigation
        if (updateInvestigation) {
          await fetchDataAndSync(
            `investigation-${clinicBrandId}`,
            `/api/secure/clinic-practitioner/investigation/list?search=${''}&clinicBrandId=${clinicBrandId}`,
            addOrUpdateInvestigationInIdb,
            investigationModifiedAfterTimeUtc,
          )
          localStorage.setItem(
            LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.INVESTIGATION,
            dayjs.utc().format('YYYY-MM-DD HH:mm:ss'),
          )
        }

        // 8. Sync patients
        if (updatePatients) {
          await fetchDataAndSync(
            `patients-${clinicBrandId}`,
            `/api/secure/patient/list?search=${''}&clinicBrandId=${clinicBrandId}`,
            addOrUpdatePatientsInIdb,
            patientsModifiedAfterTimeUtc,
          )
          localStorage.setItem(
            LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.PATIENTS,
            dayjs.utc().format('YYYY-MM-DD HH:mm:ss'),
          )
        }
        // 9. Sync psych assessments
        if (updatePsychAssessments) {
          await fetchDataAndSync(
            `psychAsssessments-${clinicBrandId}`,
            `/api/secure/clinic-practitioner/psych-assessment/list?search=${''}&clinicBrandId=${clinicBrandId}`,
            addOrUpdatePsychAssessmentsInIdb,
            psychAssessmentsModifiedAfterTimeUtc,
          )
          localStorage.setItem(
            LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.PSYCH_ASSESSMENTS,
            dayjs.utc().format('YYYY-MM-DD HH:mm:ss'),
          )
        }

        // 10. Sync generic drugs
        if (updateGenericDrugs) {
          await fetchDataAndSync(
            `genericDrugs-${clinicBrandId}`,
            `/api/secure/clinic-practitioner/drug-generic-formula/list?search=${''}&clinicBrandId=${clinicBrandId}`,
            addOrUpdateGenericDrugsInIdb,
            genericDrugsModifiedAfterTimeUtc,
          )
          localStorage.setItem(
            LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.GENERIC_DRUGS,
            dayjs.utc().format('YYYY-MM-DD HH:mm:ss'),
          )
        }
      }
      if (!Boolean(takeActionsItself)) {
        toast.success('Data Sync Completed')
      }
    } catch (error) {
      console.error('Sync failed', error)
    } finally {
      setDialogOpen(false)
      setSaving({
        drugs: false,
        complaints: false,
        diagnosis: false,
        observations: false,
        advices: false,
        instructions: false,
        investigation: false,
        patients: false,
        psychAssessments: false,
        genericDrugs: false,
      })
      setSyncProgress({
        drugs: 0,
        complaints: 0,
        diagnosis: 0,
        observations: 0,
        advices: 0,
        instructions: 0,
        investigation: 0,
        patients: 0,
        psychAssessments: 0,
        genericDrugs: 0,
      })
    }
  }

  async function fetchDataAndSync(category, url, updateFn, modifiedAfter) {
    let hasMoreData = true
    let currentPage = 1 // Use local variable instead of state

    while (hasMoreData) {
      const modifiedAfterParam = modifiedAfter
        ? `&modifiedAfter=${dayjs.utc(modifiedAfter).format('YYYY-MM-DDTHH:mm:ss')}Z`
        : ''
      const fullUrl = `${url}&page=${currentPage}&pageSize=500${modifiedAfterParam}`

      const response = await getCPDataWithoutParams(fullUrl)

      if (response?.status === 200 && Array.isArray(response?.data)) {
        if (response?.data?.length > 0) {
          await updateFn(response?.data)
          currentPage += 1
          // we do not have actual count of total data so just emulating that progress here
          if (category.startsWith('drugs')) {
            setSyncProgress((prev) => ({
              ...prev,
              [category]: Math.min(((currentPage - 1) * 100) / 200, 100),
            }))
          } else {
            setSyncProgress((prev) => ({
              ...prev,
              [category]: Math.min(((currentPage - 1) * 100) / 50, 100),
            }))
          }
        } else {
          hasMoreData = false
          setSyncProgress((prev) => ({
            ...prev,
            [category]: 100,
          }))
        }
      } else {
        hasMoreData = false
      }
    }

    // Update progress and saving state
    setSaving((prev) => ({
      ...prev,
      [category]: false,
    }))
    setSyncProgress((prev) => ({
      ...prev,
      [category]: 100,
    }))
  }

  async function handleClickForUpdate() {
    setDialogOpen(true)
    await deleteERMDatabase()
    await updateEverythingEveryBrand(
      true,
      true,
      true,
      true,
      true,
      true,
      true,
      true,
      true,
      true,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
    )
  }

  function getShouldUpdateHEAndItsModifiedAfterTime({
    lastUpdatedIdbUTCTime,
    lastUpdatedMongoTimeUTC,
    shouldUpdateHE,
    modifiedAfterTimeUTC,
  }) {
    let init = {
      shouldUpdateHE: shouldUpdateHE,
      modifiedAfterTimeUTC: modifiedAfterTimeUTC,
    }

    if (lastUpdatedIdbUTCTime) {
      if (lastUpdatedMongoTimeUTC) {
        if (dayjs.utc(lastUpdatedMongoTimeUTC).isAfter(dayjs.utc(lastUpdatedIdbUTCTime))) {
          init.shouldUpdateHE = true
          init.modifiedAfterTimeUTC = lastUpdatedIdbUTCTime
        } else {
          init.shouldUpdateHE = false
        }
      }
    } else {
      init.shouldUpdateHE = true
      init.modifiedAfterTimeUTC = null
    }
    return init
  }

  async function takeActionsItselfFunction() {
    let shouldUpdateDrugs = false
    let shouldUpdateComplaints = false
    let shouldUpdateDiagnosis = false
    let shouldUpdateObservations = false
    let shouldUpdateAdvices = false
    let shouldUpdateInstructions = false
    let shouldUpdateInvestigation = false
    let shouldUpdatePatients = false
    let shouldUpdatePsychAssessments = false
    let shouldUpdateGenericDrugs = false

    let drugsModifiedAfterTimeUtc = null
    let healthComplaintsModifiedAfterTimeUtc = null
    let diagnosisModifiedAfterTimeUtc = null
    let observationsModifiedAfterTimeUtc = null
    let advicesModifiedAfterTimeUtc = null
    let instructionsModifiedAfterTimeUtc = null
    let investigationModifiedAfterTimeUtc = null
    let patientsModifiedAfterTimeUtc = null
    let psychAssessmentsModifiedAfterTimeUtc = null
    let genericDrugsModifiedAfterTimeUtc = null

    const drugsLastUpdatedIdbUtcTime = localStorage.getItem(
      LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.DRUGS,
    )
    const complaintsLastUpdatedIdbUtcTime = localStorage.getItem(
      LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.COMPLAINTS,
    )
    const diagnosisLastUpdatedIdbUtcTime = localStorage.getItem(
      LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.DIAGNOSIS,
    )
    const observationsLastUpdatedIdbUtcTime = localStorage.getItem(
      LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.OBSERVATIONS,
    )
    const advicesLastUpdatedIdbUtcTime = localStorage.getItem(
      LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.ADVICES,
    )
    const instructionsLastUpdatedIdbUtcTime = localStorage.getItem(
      LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.INSTRUCTIONS,
    )
    const investigationLastUpdatedIdbUtcTime = localStorage.getItem(
      LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.INVESTIGATION,
    )
    const patientsLastUpdatedIdbUtcTime = localStorage.getItem(
      LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.PATIENTS,
    )
    const psychAssessmentsLastUpdatedIdbUtcTime = localStorage.getItem(
      LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.PSYCH_ASSESSMENTS,
    )
    const genericDrugsLastUpdatedIdbUtcTime = localStorage.getItem(
      LOCALSTORAGE_KEYS_IDB_STORE_LAST_UPDATED.GENERIC_DRUGS,
    )

    let drugsLastUpdatedMongoUtc = null
    let complaintsLastUpdatedMongoUtc = null
    let diagnosisLastUpdatedMongoUtc = null
    let observationsLastUpdatedMongoUtc = null
    let advicesLastUpdatedMongoUtc = null
    let instructionsLastUpdatedMongoUtc = null
    let investigationLastUpdatedMongoUtc = null
    let patientsLastUpdatedMongoUtc = null
    let psychAssessmentsLastUpdatedMongoUtc = null
    let genericDrugsLastUpdatedMongoUtc = null

    const promises = clinicBrandUniqueIds.map(async (clinicBrandId) => {
      const response = await getMPDataWithoutParams(
        `/api/secure/doctor/medical-document-update-status/list?clinicBrandId=${clinicBrandId}`,
      )

      if (response?.status === 200 && Array.isArray(response?.data)) {
        response.data.forEach((item) => {
          const lastUpdateTime = dayjs.utc(item?.lastUpdateTimeUtc)

          if (item?.documentType === 'drugs') {
            if (
              !drugsLastUpdatedMongoUtc ||
              dayjs.utc(lastUpdateTime).isAfter(dayjs.utc(drugsLastUpdatedMongoUtc))
            ) {
              drugsLastUpdatedMongoUtc = lastUpdateTime
            }
          } else if (item?.documentType === 'diagnosis') {
            if (
              !diagnosisLastUpdatedMongoUtc ||
              dayjs.utc(lastUpdateTime).isAfter(dayjs.utc(diagnosisLastUpdatedMongoUtc))
            ) {
              diagnosisLastUpdatedMongoUtc = lastUpdateTime
            }
          } else if (item?.documentType === 'health_observations') {
            if (
              !observationsLastUpdatedMongoUtc ||
              dayjs.utc(lastUpdateTime).isAfter(dayjs.utc(observationsLastUpdatedMongoUtc))
            ) {
              observationsLastUpdatedMongoUtc = lastUpdateTime
            }
          } else if (item?.documentType === 'prescription_advice') {
            if (
              !advicesLastUpdatedMongoUtc ||
              dayjs.utc(lastUpdateTime).isAfter(dayjs.utc(advicesLastUpdatedMongoUtc))
            ) {
              advicesLastUpdatedMongoUtc = lastUpdateTime
            }
          } else if (item?.documentType === 'health_complaints') {
            if (
              !complaintsLastUpdatedMongoUtc ||
              dayjs.utc(lastUpdateTime).isAfter(dayjs.utc(complaintsLastUpdatedMongoUtc))
            ) {
              complaintsLastUpdatedMongoUtc = lastUpdateTime
            }
          } else if (item?.documentType === 'instructions') {
            if (
              !instructionsLastUpdatedMongoUtc ||
              dayjs.utc(lastUpdateTime).isAfter(dayjs.utc(instructionsLastUpdatedMongoUtc))
            ) {
              instructionsLastUpdatedMongoUtc = lastUpdateTime
            }
          } else if (item?.documentType === 'investigation') {
            if (
              !investigationLastUpdatedMongoUtc ||
              dayjs.utc(lastUpdateTime).isAfter(dayjs.utc(investigationLastUpdatedMongoUtc))
            ) {
              investigationLastUpdatedMongoUtc = lastUpdateTime
            }
          } else if (item?.documentType === 'patients') {
            if (
              !patientsLastUpdatedMongoUtc ||
              dayjs.utc(lastUpdateTime).isAfter(dayjs.utc(patientsLastUpdatedMongoUtc))
            ) {
              patientsLastUpdatedMongoUtc = lastUpdateTime
            }
          } else if (item?.documentType === 'psych_assessments') {
            if (
              !psychAssessmentsLastUpdatedMongoUtc ||
              dayjs.utc(lastUpdateTime).isAfter(dayjs.utc(psychAssessmentsLastUpdatedMongoUtc))
            ) {
              psychAssessmentsLastUpdatedMongoUtc = lastUpdateTime
            }
          } else if (item?.documentType === 'drugs_generic_names') {
            if (
              !genericDrugsLastUpdatedMongoUtc ||
              dayjs.utc(lastUpdateTime).isAfter(dayjs.utc(genericDrugsLastUpdatedMongoUtc))
            ) {
              genericDrugsLastUpdatedMongoUtc = lastUpdateTime
            }
          }
        })
      }
    })

    console.log('hw-4')

    // Wait for all promises to resolve
    await Promise.all(promises)

    console.log('hw-5')

    const drugUpdateState = getShouldUpdateHEAndItsModifiedAfterTime({
      lastUpdatedIdbUTCTime: drugsLastUpdatedIdbUtcTime,
      lastUpdatedMongoTimeUTC: drugsLastUpdatedMongoUtc,
      modifiedAfterTimeUTC: drugsModifiedAfterTimeUtc,
      shouldUpdateHE: shouldUpdateDrugs,
    })
    shouldUpdateDrugs = drugUpdateState.shouldUpdateHE
    drugsModifiedAfterTimeUtc = drugUpdateState.modifiedAfterTimeUTC

    const complaintsUpdateState = getShouldUpdateHEAndItsModifiedAfterTime({
      lastUpdatedIdbUTCTime: complaintsLastUpdatedIdbUtcTime,
      lastUpdatedMongoTimeUTC: complaintsLastUpdatedMongoUtc,
      modifiedAfterTimeUTC: healthComplaintsModifiedAfterTimeUtc,
      shouldUpdateHE: shouldUpdateComplaints,
    })
    shouldUpdateComplaints = complaintsUpdateState.shouldUpdateHE
    healthComplaintsModifiedAfterTimeUtc = complaintsUpdateState.modifiedAfterTimeUTC

    const diagnosisUpdateState = getShouldUpdateHEAndItsModifiedAfterTime({
      lastUpdatedIdbUTCTime: diagnosisLastUpdatedIdbUtcTime,
      lastUpdatedMongoTimeUTC: diagnosisLastUpdatedMongoUtc,
      modifiedAfterTimeUTC: diagnosisModifiedAfterTimeUtc,
      shouldUpdateHE: shouldUpdateDiagnosis,
    })
    shouldUpdateDiagnosis = diagnosisUpdateState.shouldUpdateHE
    diagnosisModifiedAfterTimeUtc = diagnosisUpdateState.modifiedAfterTimeUTC

    const observationsUpdateState = getShouldUpdateHEAndItsModifiedAfterTime({
      lastUpdatedIdbUTCTime: observationsLastUpdatedIdbUtcTime,
      lastUpdatedMongoTimeUTC: observationsLastUpdatedMongoUtc,
      modifiedAfterTimeUTC: observationsModifiedAfterTimeUtc,
      shouldUpdateHE: shouldUpdateObservations,
    })
    shouldUpdateObservations = observationsUpdateState.shouldUpdateHE
    observationsModifiedAfterTimeUtc = observationsUpdateState.modifiedAfterTimeUTC

    const advicesUpdateState = getShouldUpdateHEAndItsModifiedAfterTime({
      lastUpdatedIdbUTCTime: advicesLastUpdatedIdbUtcTime,
      lastUpdatedMongoTimeUTC: advicesLastUpdatedMongoUtc,
      modifiedAfterTimeUTC: advicesModifiedAfterTimeUtc,
      shouldUpdateHE: shouldUpdateAdvices,
    })
    shouldUpdateAdvices = advicesUpdateState.shouldUpdateHE
    advicesModifiedAfterTimeUtc = advicesUpdateState.modifiedAfterTimeUTC

    const instructionsUpdateState = getShouldUpdateHEAndItsModifiedAfterTime({
      lastUpdatedIdbUTCTime: instructionsLastUpdatedIdbUtcTime,
      lastUpdatedMongoTimeUTC: instructionsLastUpdatedMongoUtc,
      modifiedAfterTimeUTC: instructionsModifiedAfterTimeUtc,
      shouldUpdateHE: shouldUpdateInstructions,
    })
    shouldUpdateInstructions = instructionsUpdateState.shouldUpdateHE
    instructionsModifiedAfterTimeUtc = instructionsUpdateState.modifiedAfterTimeUTC

    const investigationUpdateState = getShouldUpdateHEAndItsModifiedAfterTime({
      lastUpdatedIdbUTCTime: investigationLastUpdatedIdbUtcTime,
      lastUpdatedMongoTimeUTC: investigationLastUpdatedMongoUtc,
      modifiedAfterTimeUTC: investigationModifiedAfterTimeUtc,
      shouldUpdateHE: shouldUpdateInvestigation,
    })
    shouldUpdateInvestigation = investigationUpdateState.shouldUpdateHE
    investigationModifiedAfterTimeUtc = investigationUpdateState.modifiedAfterTimeUTC

    const patientsUpdateState = getShouldUpdateHEAndItsModifiedAfterTime({
      lastUpdatedIdbUTCTime: patientsLastUpdatedIdbUtcTime,
      lastUpdatedMongoTimeUTC: patientsLastUpdatedMongoUtc,
      modifiedAfterTimeUTC: patientsModifiedAfterTimeUtc,
      shouldUpdateHE: shouldUpdatePatients,
    })
    shouldUpdatePatients = patientsUpdateState.shouldUpdateHE
    patientsModifiedAfterTimeUtc = patientsUpdateState.modifiedAfterTimeUTC

    const psychAssessmentsUpdateState = getShouldUpdateHEAndItsModifiedAfterTime({
      lastUpdatedIdbUTCTime: psychAssessmentsLastUpdatedIdbUtcTime,
      lastUpdatedMongoTimeUTC: psychAssessmentsLastUpdatedMongoUtc,
      modifiedAfterTimeUTC: psychAssessmentsModifiedAfterTimeUtc,
      shouldUpdateHE: shouldUpdatePsychAssessments,
    })
    shouldUpdatePsychAssessments = psychAssessmentsUpdateState.shouldUpdateHE
    psychAssessmentsModifiedAfterTimeUtc = psychAssessmentsUpdateState.modifiedAfterTimeUTC

    const genericDrugsUpdateState = getShouldUpdateHEAndItsModifiedAfterTime({
      lastUpdatedIdbUTCTime: genericDrugsLastUpdatedIdbUtcTime,
      lastUpdatedMongoTimeUTC: genericDrugsLastUpdatedMongoUtc,
      modifiedAfterTimeUTC: genericDrugsModifiedAfterTimeUtc,
      shouldUpdateHE: shouldUpdateGenericDrugs,
    })
    shouldUpdateGenericDrugs = genericDrugsUpdateState.shouldUpdateHE
    genericDrugsModifiedAfterTimeUtc = genericDrugsUpdateState.modifiedAfterTimeUTC

    if (
      drugsLastUpdatedIdbUtcTime &&
      complaintsLastUpdatedIdbUtcTime &&
      diagnosisLastUpdatedIdbUtcTime &&
      observationsLastUpdatedIdbUtcTime &&
      advicesLastUpdatedIdbUtcTime &&
      instructionsLastUpdatedIdbUtcTime &&
      investigationLastUpdatedIdbUtcTime &&
      patientsLastUpdatedIdbUtcTime &&
      psychAssessmentsLastUpdatedIdbUtcTime &&
      genericDrugsLastUpdatedIdbUtcTime
    ) {
      setDialogOpen(false)
    } else {
      setDialogOpen(true)
    }

    console.log(shouldUpdateDrugs)
    console.log(shouldUpdateComplaints)
    console.log(shouldUpdateDiagnosis)
    console.log(shouldUpdateObservations)
    console.log(shouldUpdateAdvices)
    console.log(shouldUpdateInstructions)
    console.log(shouldUpdateInvestigation)
    console.log(shouldUpdatePatients)
    console.log(drugsModifiedAfterTimeUtc)
    console.log(healthComplaintsModifiedAfterTimeUtc)
    console.log(diagnosisModifiedAfterTimeUtc)
    console.log(instructionsModifiedAfterTimeUtc)
    console.log(investigationModifiedAfterTimeUtc)
    console.log(patientsModifiedAfterTimeUtc)
    console.log(psychAssessmentsModifiedAfterTimeUtc)
    console.log(genericDrugsModifiedAfterTimeUtc)

    await updateEverythingEveryBrand(
      shouldUpdateDrugs,
      shouldUpdateComplaints,
      shouldUpdateDiagnosis,
      shouldUpdateObservations,
      shouldUpdateAdvices,
      shouldUpdateInstructions,
      shouldUpdateInvestigation,
      shouldUpdatePatients,
      shouldUpdatePsychAssessments,
      shouldUpdateGenericDrugs,
      drugsModifiedAfterTimeUtc,
      healthComplaintsModifiedAfterTimeUtc,
      diagnosisModifiedAfterTimeUtc,
      observationsModifiedAfterTimeUtc,
      advicesModifiedAfterTimeUtc,
      instructionsModifiedAfterTimeUtc,
      investigationModifiedAfterTimeUtc,
      patientsModifiedAfterTimeUtc,
      psychAssessmentsModifiedAfterTimeUtc,
      genericDrugsModifiedAfterTimeUtc,
    )
  }

  useEffect(() => {
    if (takeActionsItself) {
      takeActionsItselfFunction()
    }
  }, [])

  return (
    <div>
      {!Boolean(takeActionsItself) && (
        <div>
          <div style={{ marginBottom: '16px' }}>
            <b>Update Local Data Options (Drugs/Diagnosis/etc.)</b>
          </div>
          <button className="btn btn-primary" onClick={handleClickForUpdate}>
            Update Now
          </button>
        </div>
      )}
      {dialogOpen && (
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <div
            style={{
              padding: '16px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              minWidth: '400px',
            }}
          >
            <div style={{ padding: '16px 0 48px' }}>Syncing Data...</div>

            <div>
              {clinicBrandUniqueIds.map((id) => (
                <div key={id} style={{ marginBottom: '24px' }}>
                  <h4>{formatClinicBrandId(id)}</h4>
                  <p>
                    Drugs Sync Progress: {syncProgress[`drugs-${id}`] || 0}%
                    <LinearProgress
                      variant="determinate"
                      value={syncProgress[`drugs-${id}`] || 0}
                    />
                  </p>
                  <p>
                    Complaints Sync Progress: {syncProgress[`complaints-${id}`] || 0}%
                    <LinearProgress
                      variant="determinate"
                      value={syncProgress[`complaints-${id}`] || 0}
                    />
                  </p>
                  <p>
                    Diagnosis Sync Progress: {syncProgress[`diagnosis-${id}`] || 0}%
                    <LinearProgress
                      variant="determinate"
                      value={syncProgress[`diagnosis-${id}`] || 0}
                    />
                  </p>
                  <p>
                    Observations Sync Progress: {syncProgress[`observations-${id}`] || 0}%
                    <LinearProgress
                      variant="determinate"
                      value={syncProgress[`observations-${id}`] || 0}
                    />
                  </p>
                  <p>
                    Advices Sync Progress: {syncProgress[`advices-${id}`] || 0}%
                    <LinearProgress
                      variant="determinate"
                      value={syncProgress[`advices-${id}`] || 0}
                    />
                  </p>
                  <p>
                    Instructions Sync Progress: {syncProgress[`instruction-${id}`] || 0}%
                    <LinearProgress
                      variant="determinate"
                      value={syncProgress[`instruction-${id}`] || 0}
                    />
                  </p>
                  <p>
                    Investigation Sync Progress: {syncProgress[`investigation-${id}`] || 0}%
                    <LinearProgress
                      variant="determinate"
                      value={syncProgress[`investigation-${id}`] || 0}
                    />
                  </p>
                  <p>
                    Patients Sync Progress: {syncProgress[`patients-${id}`] || 0}%
                    <LinearProgress
                      variant="determinate"
                      value={syncProgress[`patients-${id}`] || 0}
                    />
                  </p>
                  <p>
                    Psych Assessments Sync Progress: {syncProgress[`psychAssessments-${id}`] || 0}%
                    <LinearProgress
                      variant="determinate"
                      value={syncProgress[`psychAssessments-${id}`] || 0}
                    />
                  </p>
                  <p>
                    Generic Drugs Sync Progress: {syncProgress[`genericDrugs-${id}`] || 0}%
                    <LinearProgress
                      variant="determinate"
                      value={syncProgress[`genericDrugs-${id}`] || 0}
                    />
                  </p>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
              <button
                style={{
                  background: '#187ff2',
                  borderRadius: '24px',
                  padding: '8px 16px',
                  color: '#fff',
                }}
                disabled={
                  saving?.drugs ||
                  saving?.complaints ||
                  saving?.diagnosis ||
                  saving?.observations ||
                  saving?.advices ||
                  saving?.instructions ||
                  saving?.patients ||
                  saving?.psychAssessments ||
                  saving?.genericDrugs
                }
              >
                {saving?.drugs ||
                saving?.complaints ||
                saving?.diagnosis ||
                saving?.observations ||
                saving?.advices ||
                saving?.instructions ||
                saving?.investigation ||
                saving?.patients ||
                saving?.psychAssessments ||
                saving?.genericDrugs
                  ? ' Saving '
                  : ' Save '}
                {(saving?.drugs ||
                  saving?.complaints ||
                  saving?.diagnosis ||
                  saving?.observations ||
                  saving?.advices ||
                  saving?.instructions ||
                  saving?.investigation ||
                  saving?.patients ||
                  saving?.psychAssessments ||
                  saving?.genericDrugs) && <Spinner animation="border" variant="light" size="sm" />}
              </button>
            </div>
          </div>
          <button
            style={{
              background: 'linear-gradient(145deg, #6e7dff, #1e3bb1)',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: '24px',
              width: 'fit-content',
              margin: '0 auto 24px',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            }}
            onClick={() => setDialogOpen(false)}
          >
            Sync in background
          </button>
        </Dialog>
      )}
    </div>
  )
}
